import { AlertCircle, CheckCircle2, Inbox, LoaderCircle, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { displayError, statusTone } from "../lib/format";
import type { ToastPayload } from "../lib/notify";

export function StatusBadge({ value }: { value: unknown }) {
  const label = String(value ?? "UNKNOWN").replaceAll("_", " ");
  return <span className={`status-badge status-${statusTone(value)}`}>{label}</span>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function LoadingState({ label = "正在加载" }: { label?: string }) {
  return <div className="state-block"><LoaderCircle className="spin" size={20} /><span>{label}</span></div>;
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div className="state-block state-error">
      <AlertCircle size={20} />
      <span>{displayError(error)}</span>
      {onRetry && <button className="button button-secondary button-small" onClick={onRetry}>重试</button>}
    </div>
  );
}

export function EmptyState({ label = "暂无数据" }: { label?: string }) {
  return <div className="state-block"><Inbox size={20} /><span>{label}</span></div>;
}

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
  width = "medium",
}: {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  width?: "small" | "medium" | "large";
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal modal-${width}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header className="modal-header">
          <div>
            <h2 id="modal-title">{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

export function ToastHost() {
  const [toasts, setToasts] = useState<ToastPayload[]>([]);
  useEffect(() => {
    const onToast = (event: Event) => {
      const toast = (event as CustomEvent<ToastPayload>).detail;
      setToasts((current) => [...current.slice(-3), toast]);
      window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== toast.id)), 4200);
    };
    window.addEventListener("admin:toast", onToast);
    return () => window.removeEventListener("admin:toast", onToast);
  }, []);
  return (
    <div className="toast-host" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.tone}`}>
          {toast.tone === "success" ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
          <span>{toast.message}</span>
          <button className="icon-button" onClick={() => setToasts((current) => current.filter((item) => item.id !== toast.id))} aria-label="关闭通知">
            <X size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}
